import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { IAnalysedProperty } from "../../../interface/addAnalyser";
import OtherPurchaseExpense from "./Child/OtherPurchaseExpenses";
import PurchaseDetails from "./Child/PurchaseDetails";
import LoanDetails from "./Child/LoanDetails";
import { useAnalysedPropertyContext } from "./Child/AnalysedPropertyContext";
import { formatCurrency } from "../../../utils/commonUtil";
import { REIProgressSpinner } from "../../../components/ui";

interface LoanPurchaseCostProps {
  setIsValid: (isValid: boolean) => void;
  updateProperty: (updated: Partial<IAnalysedProperty>) => void;
}

const LoanPurchaseCost = forwardRef<{ validate: () => boolean }, LoanPurchaseCostProps>(
  ({ setIsValid, updateProperty }, ref) => {
    const { analysedProperty } = useAnalysedPropertyContext();
    const [loanPurchaseCost, setLoanPurchaseCost] = useState<number>(0);
    const [loanAmount, setLoanAmount] = useState<number>(0);
    const [exceedAmount, setExceedAmount] = useState<number>(0);
    const purchaseDetailsRef = useRef<any>(null);
    const loanDetailsRef = useRef<any>(null);
    const hasLoanPurchaseCostData =
      !!analysedProperty?.LoanTypeCost?.PropertyPurchaseCost &&
      !!analysedProperty?.LoanTypeCost?.PropertyOtherPurchaseExpense &&
      Array.isArray(analysedProperty?.LoanTypeCost?.Loans) &&
      analysedProperty.LoanTypeCost.Loans.length > 0;

    useImperativeHandle(ref, () => ({
      validate: () => {
        const isPurchaseValid = purchaseDetailsRef.current?.validate?.() ?? false;
        const isLoanValid = loanDetailsRef.current?.validate?.() ?? false;
        return isPurchaseValid && isLoanValid;
      },
    }));

    useEffect(() => {
      if (hasLoanPurchaseCostData && analysedProperty) {
        var totalPurchaseCost = 0;
        totalPurchaseCost =
          (analysedProperty.LoanTypeCost.PropertyPurchaseCost.PurchaseAmount ?? 0) +
          analysedProperty.LoanTypeCost.PropertyOtherPurchaseExpense.CapitalInvestment +
          analysedProperty.LoanTypeCost.PropertyOtherPurchaseExpense.OtherCost +
          analysedProperty.LoanTypeCost.PropertyOtherPurchaseExpense.SolicitorsCost +
          analysedProperty.LoanTypeCost.PropertyOtherPurchaseExpense.StampDuty;

        analysedProperty.LoanTypeCost.Loans.forEach((loan) => {
          totalPurchaseCost += loan.EstablishmentFees + loan.OtherLoanCosts + loan.ValuationFees;
        });

        var totalPurchaseCostDefaultLoan = 0;
        totalPurchaseCostDefaultLoan =
          (analysedProperty.LoanTypeCost.PropertyPurchaseCost.PurchaseAmount ?? 0) +
          analysedProperty.LoanTypeCost.PropertyOtherPurchaseExpense.CapitalInvestment +
          analysedProperty.LoanTypeCost.PropertyOtherPurchaseExpense.OtherCost +
          analysedProperty.LoanTypeCost.PropertyOtherPurchaseExpense.SolicitorsCost +
          analysedProperty.LoanTypeCost.PropertyOtherPurchaseExpense.StampDuty +
          analysedProperty.LoanTypeCost.Loans[0].EstablishmentFees +
          analysedProperty.LoanTypeCost.Loans[0].OtherLoanCosts +
          analysedProperty.LoanTypeCost.Loans[0].ValuationFees;

        const purchaseAmount = analysedProperty.LoanTypeCost.PropertyPurchaseCost.PurchaseAmount;
        if (purchaseAmount === undefined || purchaseAmount === null || purchaseAmount === 0) {
          setLoanAmount(0);
        } else {
          const cashDeposit = analysedProperty.LoanTypeCost.PropertyPurchaseCost.CashDeposit;
          if (analysedProperty.LoanTypeCost.PropertyPurchaseCost.isEnterPercentage == "1") {
            setLoanAmount(totalPurchaseCostDefaultLoan - purchaseAmount * (cashDeposit / 100));
          } else {
            setLoanAmount(totalPurchaseCostDefaultLoan - cashDeposit);
          }
        }
        setLoanPurchaseCost(totalPurchaseCost);
      }
    }, [analysedProperty?.LoanTypeCost, hasLoanPurchaseCostData]);

    useEffect(() => {
      if (loanPurchaseCost > 0 && hasLoanPurchaseCostData && analysedProperty) {
        const purchaseAmount = analysedProperty.LoanTypeCost.PropertyPurchaseCost.PurchaseAmount;

        const cashDeposit = analysedProperty.LoanTypeCost.PropertyPurchaseCost.CashDeposit;
        var depositAmount = 0;
        if (analysedProperty.LoanTypeCost.PropertyPurchaseCost.isEnterPercentage == "1") {
          depositAmount = (purchaseAmount ?? 0) * (cashDeposit / 100);
        } else {
          depositAmount = cashDeposit;
        }
        setExceedAmount(analysedProperty?.LoanTypeCost.Loans[0].Amount + depositAmount - loanPurchaseCost);
      }
    }, [loanPurchaseCost, analysedProperty?.LoanTypeCost, hasLoanPurchaseCostData]);

    if (!hasLoanPurchaseCostData) {
      return (
        <div className="w-full text-center">
          <REIProgressSpinner />
        </div>
      );
    }

    return (
      <>
        <PurchaseDetails ref={purchaseDetailsRef} setIsValid={setIsValid} updateProperty={updateProperty} />
        <OtherPurchaseExpense setIsValid={setIsValid} updateProperty={updateProperty} />
        <LoanDetails
          ref={loanDetailsRef}
          setIsValid={setIsValid}
          updateProperty={updateProperty}
          loanAmount={loanAmount}
        />
        <div className="w-full">
          <div className="relative rounded-lg pl-3 pr-1 py-3 border-solid border border-gray-300 bg-white mt-2">
            <div className="">
              <div className="flex flex-col lg:flex-row lg:w-full gap-2">
                <div className="flex-0 flex-col w-full lg:w-1/3 gap-2">
                  <span className="font-semibold">Total purchase costs:</span>
                </div>
                <div className="flex-0 flex-col w-full lg:w-1/3 gap-2">
                  <span>{formatCurrency(loanPurchaseCost)}</span>
                </div>
                <div className="flex-0 flex-col w-full lg:w-1/3 gap-2">
                  {exceedAmount > 0 && (
                    <span className="">
                      {`Deposit and loans are ${formatCurrency(exceedAmount)} more than Purchase Costs.`}
                    </span>
                  )}
                  {exceedAmount < 0 && (
                    <span className="">
                      {`Deposit and loans are ${formatCurrency(-exceedAmount)} less than Purchase Costs.`}
                    </span>
                  )}
                  {exceedAmount === 0 && <span className="">{`Deposit and loans are equal to Purchase Costs.`}</span>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
);

export default LoanPurchaseCost;
